import { useRef, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { useQuery } from '@tanstack/react-query'
import { getItemsByRequest } from '../../../shared/api/requestItems.api'

interface SiblingDropdownProps {
  itemId: string
  requestId: string
  role: string | undefined
  userId: string | undefined
  show: boolean
  onToggle: () => void
  onClose: () => void
}

export function SiblingDropdown({
  itemId,
  requestId,
  role,
  userId,
  show,
  onToggle,
  onClose,
}: SiblingDropdownProps) {
  const navigate = useNavigate()
  const siblingRef = useRef<HTMLDivElement>(null)

  const { data: siblingItems = [] } = useQuery({
    queryKey: ['request-items-by-request', requestId],
    queryFn: () => getItemsByRequest(requestId),
    enabled: !!requestId && show,
  })

  useEffect(() => {
    if (!show) return
    const handler = (e: MouseEvent) => {
      if (siblingRef.current && !siblingRef.current.contains(e.target as Node)) {
        onClose()
      }
    }
    document.addEventListener('mousedown', handler)
    return () => document.removeEventListener('mousedown', handler)
  }, [show, onClose])

  const filteredSiblings = siblingItems.filter((s) => {
    if (role === 'PROCUREMENT') return s.matrix === 'Китай' || s.id === itemId
    return true
  })

  const hasMyQuote = (s: (typeof siblingItems)[number]): boolean => {
    if (role === 'PROCUREMENT') {
      return !!s.results?.some((r) => (r.creator?.id ?? r.createdBy) === userId)
    }
    return !!s._count && s._count.results > 0
  }

  return (
    <div className="relative" ref={siblingRef}>
      <button
        type="button"
        onClick={onToggle}
        title="Переключиться на другую позицию"
        className="inline-flex items-center justify-center w-6 h-6 rounded hover:bg-gray-100 text-gray-400 hover:text-gray-700 transition-colors"
        aria-label="Другие позиции заявки"
      >
        <svg
          className={`w-3.5 h-3.5 transition-transform ${show ? 'rotate-180' : ''}`}
          viewBox="0 0 20 20"
          fill="currentColor"
        >
          <path
            fillRule="evenodd"
            d="M5.23 7.21a.75.75 0 011.06.02L10 11.06l3.71-3.83a.75.75 0 111.08 1.04l-4.25 4.4a.75.75 0 01-1.08 0l-4.25-4.4a.75.75 0 01.02-1.06z"
            clipRule="evenodd"
          />
        </svg>
      </button>

      {show && (
        <div className="absolute left-0 top-full mt-1 z-50 bg-white border border-gray-200 rounded-xl shadow-lg min-w-[280px] max-h-80 overflow-y-auto">
          <div className="px-3 py-2 border-b border-gray-100 text-xs text-gray-400 font-medium sticky top-0 bg-white">
            Позиции заявки
          </div>
          {filteredSiblings.length === 0 ? (
            <div className="px-3 py-3 text-xs text-gray-400">Нет позиций</div>
          ) : (
            filteredSiblings.map((s) => {
              const isCurrent = s.id === itemId
              const quoted = hasMyQuote(s)
              return (
                <button
                  key={s.id}
                  onClick={() => {
                    onClose()
                    if (!isCurrent) navigate(`/request-items/${s.id}`)
                  }}
                  disabled={isCurrent}
                  className={`w-full text-left px-3 py-2 transition-colors flex items-center gap-2 border-b border-gray-50 last:border-0 ${
                    isCurrent ? 'bg-brand-50 cursor-default' : 'hover:bg-gray-50 cursor-pointer'
                  }`}
                >
                  <span
                    className={`text-green-600 text-xs font-semibold w-4 shrink-0 text-center ${
                      quoted ? '' : 'invisible'
                    }`}
                    title={quoted ? 'Есть квота' : ''}
                  >
                    ✓
                  </span>
                  <span
                    className={`font-mono text-xs truncate flex-1 ${
                      isCurrent ? 'font-bold text-brand-700' : 'font-medium text-gray-800'
                    }`}
                  >
                    {s.partNumber}
                  </span>
                  <span className="text-xs text-gray-400 shrink-0">{s.brand ?? ''}</span>
                </button>
              )
            })
          )}
        </div>
      )}
    </div>
  )
}
